import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import OAuthButton from './OAuthButton';

function Login() {

    // Sign-in is keyed on email, not username (D40) - usernames are display
    // names now and are not unique.
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { login } = useAuth();
    const navigate = useNavigate();

    const signIn = async (e) => {
        if (e) {
            e.preventDefault();
        }
        
        setError("");
        setIsSubmitting(true);
        
        try {
            await login(email, password);
            navigate("/");
        } catch (err) {
            console.log('Login error:', err);
            setError('Login failed. Please check your email and password and try again.');
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !isSubmitting && isFormValid) {
            signIn(e);
        }
    }

    // Check if both fields have valid values
    const isFormValid = email.trim().length > 0 && password.trim().length > 0;

    return (
        <div
            onClick={() => navigate("/")}
            className="flex-grow w-full flex items-center justify-center p-3 relative z-10"
        >
        <div className="max-w-md w-full">
          {/* Decorative close affordance - hidden for now. The backdrop behind
              it handles the dismiss, so this is deliberately not interactive. */}
          {/*
          <div className="flex justify-end mb-2">
            <div
              aria-hidden="true"
              className="w-7 h-7 flex items-center justify-center bg-nlg-gold text-black rounded-full pointer-events-none"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </div>
          </div>
          */}
            <section
                id="login"
                onClick={(e) => e.stopPropagation()}
                className="w-full bg-nlg-block border border-nlg-rule rounded-2xl p-5 sm:p-7"
            >
                <div className="text-center mb-6">
                    <h3 className="font-display font-black text-nlg-cream text-xl sm:text-2xl mb-1">Sign in to track scores and play with friends</h3>
                </div>
                {/* OAuth Buttons */}
                <div className="oauth-buttons space-y-3">
                    {/* OAuth Provider Buttons */}
                    <OAuthButton provider="google" label="Continue with Google" icon="/svgs/google.svg" />
                    {/* <OAuthButton provider="discord" label="Continue with Discord" icon="/svgs/discord.svg" /> */}
                    {/* <OAuthButton provider="microsoft" label="Continue with Microsoft" icon="/svgs/microsoft.svg" /> */}
                </div>

                <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-nlg-rule"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-nlg-block text-nlg-muted font-medium">OR</span>
                    </div>
                </div>

                <div className="space-y-4">
                    <div>
                        <label htmlFor="login-email" className="block text-nlg-text font-medium mb-1">Email</label>
                        <input
                            id="login-email"
                            type="email"
                            autoComplete="email"
                            placeholder="you@example.com"
                            value={email}
                            onChange={(e)=>{ setEmail(e.target.value) }}
                            onKeyPress={handleKeyPress}
                            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-nlg-rule text-nlg-text placeholder:text-nlg-muted focus:outline-none focus:border-nlg-gold/50"
                        />
                    </div>

                    <div>
                        <label htmlFor="login-password" className="block text-nlg-text font-medium mb-1">Password</label>
                        <input
                            type="password"
                            id="login-password"
                            placeholder="••••••••"
                            value={password}
                            onChange={(e)=>{ setPassword(e.target.value) }}
                            onKeyPress={handleKeyPress}
                            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-nlg-rule text-nlg-text placeholder:text-nlg-muted focus:outline-none focus:border-nlg-gold/50"
                        />
                    </div>

                    {error && (
                        <div className="bg-nlg-coral/10 border border-nlg-coral/30 text-nlg-coral px-3 py-2 rounded-lg text-sm text-center" role="alert">
                            {error}
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={signIn}
                        disabled={isSubmitting || !isFormValid}
                        className={`w-full px-4 py-2 rounded-full font-bold transition-transform duration-150 flex items-center justify-center ${
                            isFormValid && !isSubmitting
                                ? 'bg-nlg-gold text-nlg-bg hover:scale-[1.02]'
                                : 'bg-white/5 border border-nlg-rule text-nlg-muted cursor-not-allowed'
                        }`}
                    >
                        {isSubmitting ? (
                            <>
                                <div className="w-4 h-4 mr-2 rounded-full border-2 border-nlg-bg/30 border-t-nlg-bg animate-spin"></div>
                                Signing In...
                            </>
                        ) : (
                            'Sign In'
                        )}
                    </button>

                    <div className="text-center space-y-1">
                        <p className="text-sm text-nlg-muted">
                            Don't have an account?{' '}
                            <Link to="/signup" className="text-nlg-gold font-semibold transition-colors duration-150">
                                Sign Up
                            </Link>
                        </p>
                        <p className="text-sm text-nlg-muted">
                            <Link to="/reset-password" className="text-nlg-gold font-semibold transition-colors duration-150">
                                Forgot your password?
                            </Link>
                        </p>
                    </div>
                </div>
            </section>
        </div>
        </div>
    );
}

export default Login;